import { IObservable, Subject } from '~/rage/utils';
import {
	ClientError,
	ClientErrorCode,
	INumberPlatePrice,
	VehicleNumberPlate,
	VehicleNumberPlateType,
	WalletType,
	calculateNumberPlatePrice,
} from '~/shared';
import { IStorageNumberPlate, IVehicleNumberPlateService } from './types';

export class VehicleNumberPlateServiceStub
	implements IVehicleNumberPlateService
{
	private vehicleNumberPlate = new Subject<VehicleNumberPlate>({
		type: VehicleNumberPlateType.Classic,
		text: 'O111HH',
		region: '22',
	});
	private storedNumberPlates = new Subject<IStorageNumberPlate[]>([
		{
			type: VehicleNumberPlateType.Classic,
			text: 'A001AA',
			region: '77',
			createdAt: new Date(),
			isInStorage: true,
			inStorageExpiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
			onModeration: true,
		},
		{
			type: VehicleNumberPlateType.Classic,
			text: 'E256OK',
			region: '78',
			createdAt: new Date(),
			isInStorage: true,
			inStorageExpiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 21),
		},
		{
			type: VehicleNumberPlateType.ClassicNoFlag,
			text: 'A138AA',
			region: '38',
			createdAt: new Date(),
			isInStorage: true,
			inStorageExpiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 21),
		},
	]);

	public async buyNumberPlate(
		numberPlate: VehicleNumberPlate,
		wallet: WalletType,
	): Promise<void> {
		console.log('buyNumberPlate', {
			numberPlate,
			wallet,
		});
		this.vehicleNumberPlate.next(numberPlate);
	}

	public async restoreNumberPlate() {
		console.log('Restore number plate');
	}

	public async getNumberPlatePrice(
		numberPlate: VehicleNumberPlate,
		wallet: WalletType,
	): Promise<INumberPlatePrice | null> {
		return calculateNumberPlatePrice(
			numberPlate,
			{
				buyPriceBase: 1000,
				buyPrice3Letters: 1000,
				buyPrice3Numbers: 1000,
				donateBuyPriceBase: 2000,
				donateBuyPrice3Letters: 2000,
				donateBuyPrice3Numbers: 2000,
			},
			wallet === WalletType.Donate,
		);
	}

	public async moveNumberPlateToStorage(): Promise<void> {
		const currentPlate = this.vehicleNumberPlate.getValue();

		this.storedNumberPlates.next([
			...this.storedNumberPlates.getValue(),
			{
				...currentPlate,
				createdAt: new Date(),
				isInStorage: true,
				inStorageExpiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 10),
			},
		]);

		this.vehicleNumberPlate.next({
			...currentPlate,
			type: VehicleNumberPlateType.Classic,
			text: 'A001AA',
			region: '77',
		});
	}

	public moveNumberPlateFromStorage(
		numberPlate: VehicleNumberPlate,
		deleteCurrent: boolean,
	): Promise<void> {
		if (
			!this.storedNumberPlates
				.getValue()
				.find(
					(plate) =>
						plate.type === numberPlate.type &&
						plate.text === numberPlate.text &&
						plate.region === numberPlate.region,
				)
		) {
			return Promise.reject(
				new ClientError(ClientErrorCode.VehicleNumberPlateNotFound),
			);
		}

		this.storedNumberPlates.next(
			this.storedNumberPlates
				.getValue()
				.filter(
					(plate) =>
						plate.type !== numberPlate.type &&
						plate.text === numberPlate.text &&
						plate.region === numberPlate.region,
				),
		);
		if (!deleteCurrent) {
			this.storedNumberPlates.next([
				...this.storedNumberPlates.getValue(),
				{
					...this.vehicleNumberPlate.getValue(),
					createdAt: new Date(),
					isInStorage: true,
					inStorageExpiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 10),
				},
			]);
		}
		this.vehicleNumberPlate.next(numberPlate);

		return Promise.resolve();
	}

	public cancelNamedNumberPlateChange(): Promise<void> {
		this.storedNumberPlates.next(
			this.storedNumberPlates.getValue().filter((plate) => !plate.onModeration),
		);

		return Promise.resolve();
	}

	public getStoredNumberPlates(): IObservable<IStorageNumberPlate[]> {
		return this.storedNumberPlates;
	}

	public getStoreNumberPlatePrice(): number {
		return 100;
	}

	public getStoreNumberPlateMaxDays(): number {
		return 30;
	}

	public async previewNumberPlate(
		numberPlate: VehicleNumberPlate,
	): Promise<void> {
		console.log('Preview number plate:', numberPlate);
	}

	public async resetPreviewNumberPlate(): Promise<void> {
		console.log('Reset number plate preview');
	}

	public getRegionNameByCode(code: string): string | null {
		if (!code || code === '00') {
			return null;
		}

		return `Регион ${code}`;
	}

	public getLocalOwnedVehicleNumberPlate(): IObservable<VehicleNumberPlate | null> {
		return this.vehicleNumberPlate;
	}

	public async exitMenu(): Promise<void> {
		await this.resetPreviewNumberPlate();
		console.log('Exit menu');
	}
}
